
import axios from "axios"

import { mergeDeep, getPathReference, unmappObject, clone } from "../utils/editor"

export default {
  namespaced: true,

  state: () => ({
    website: null,
    websiteUid: null,
    currentPage: null,
    availablePackages: [],
    page: null,
    iframeTarget: null,
    states: {},

    loading: true,
    loadingPackages: true,
    loaded: false,
    ready: false,

    inital_colors_config: null,
    initial_config: null,

    containers: [],
    config: null,
    restartTrigger: false,
    triggerBlocksRefetch: false,
    specificBlockToRefetch: null,

    saveListeners: [],
    loaderListeners: [],
    overrides: [],
    blockReload: {},
    containerOptions: {
      iframeRect: null,
      bind: null,
      component: null,
      target: null,
    },
    context: null,
    iframeClickTrigger: null,
    blockHover: null,
    lastHover: null,
    isTeleportingConfigurationToSidebar: false,
    startingFromScratch: false,
  }),
  mutations: {
    startingFromScratch(state, startingFromScratch) {
      state.startingFromScratch = startingFromScratch
    },
    isTeleportingConfigurationToSidebar(state, isTeleportingConfigurationToSidebar) {
      state.isTeleportingConfigurationToSidebar = isTeleportingConfigurationToSidebar
    },
    iframeClick(state) {
      state.iframeClickTrigger = !state.iframeClickTrigger
    },
    triggerContainerOptions(state, rect) {
      state.containerOptions = mergeDeep(state.containerOptions || {}, rect)
    },
    closeContainerOptions(state) {
      state.containerOptions = null
    },

    blockHover(state, blockId) {
      const oldBlockId = state.blockHover
      state.blockHover = blockId
      if (blockId === null) {
        state.lastHover = oldBlockId
      }
    },
    iframeTarget(state, target) {
      state.iframeTarget = target
    },
    triggerBlocksRefetch(state, blockId) {
      state.triggerBlocksRefetch = !state.triggerBlocksRefetch
      state.specificBlockToRefetch = blockId || null
    },
    restart(state) {
      state.restartTrigger = !state.restartTrigger
      state.magentoLoaded = false
      state.ready = false
    },
    currentPage(state, currentPageConfig) {
      state.currentPage = currentPageConfig
    },
    nonRecursiveConfig(state, config) {
      console.log("mutation nonRecursiveConfig: ", clone(config))
      state.config = config
    },
    config(state, config) {
      console.log("mutation config: ", clone(config), clone({...mergeDeep(state.config, config)}))
      state.config = {...mergeDeep(state.config, config)}
    },
    overwriteConfig(state, {path, value}) {
      console.log("mutation config overwrite: ", {path, value})
      let target = state.config
      const split = path.split(".")

      split.forEach((key, index) => {
        if (index === split.length - 1) {
          target[key] = value
        } else {
          target = target[key]
        }
      })
    },

    resetColorConfig(state) {
      try {
        state.config.tailwindConfig.extend.colors = {...state.inital_colors_config}
      } catch (err) {}
    },
    resetAllConfigChanges(state) {
      state.config = {...state.initial_config}
    },
    ready(state, containers) {
      state.containers = containers
      state.ready = true
    },
    magentoLoaded(state) {
      state.magentoLoaded = true
    },
    page(state, page) {
      state.page = page
    },
    overrides(state, overrides) {
      state.overrides = overrides
    },
  },
  getters: {
    emptyConfig(state, getters) {
      console.log("startFromScratch: ", state.startingFromScratch)
      return !state.startingFromScratch && !state.loading && (getters['config'] == null || !getters['config'].blocks_collection || Object.keys(getters['config'].blocks_collection).length === 0)
    },
    overrides(state) {
      return state.overrides
    },
    isTeleportingConfigurationToSidebar(state) {
      return state.isTeleportingConfigurationToSidebar
    },
    iframeClickTrigger(state) {
      return state.iframeClickTrigger
    },
    states(state) {
      return state.states
    },
    specificBlockToRefetch(state) {
      return state.specificBlockToRefetch
    },
    iframeTarget(state) {
      return state.iframeTarget
    },
    blockHover(state) {
      return state.blockHover
    },
    lastHover(state) {
      return state.lastHover
    },
    context(state) {
      return state.context
    },
    websiteUid(state) {
      return state.websiteUid
    },
    blockReload(state) {
      return state.blockReload
    },
    currentPage(state) {
      return state.currentPage
    },
    encodedTailwindConfig(state) {
      const config = state.config && state.config.tailwindConfig
      return config ? encodeURIComponent(JSON.stringify(config)) : ""
    },
    numberOfBlocks(state) {
      const blocks = (state.config && state.config.blocks_collection) || {}

      return Object.keys(blocks).reduce((tot, page) => {
        return tot + Object.keys(blocks[page]).reduce((pageTot, role) => {
          return blocks[page][role].length + pageTot
        }, 0)
      }, 0)
    },
    triggerBlocksRefetch(state) {
      return state.triggerBlocksRefetch
    },
    blocksCollection(state, getters) {
      const config = getters.config
      return config && config.blocks_collection && config.blocks_collection
    },
    sources(state, getters) {
      const config = getters.config
      const overrides = state.overrides || []
      const currentPage = state.page
      if (!config) return []

      const sources = []
      for (const page of overrides) {
        sources.push(config.blocks_collection[page.id])
        if (currentPage === page.id) break
      }

      return [config.blocks_collection.default, ...sources]
    },
    pageContainers(state, getters) {
      const config = getters.config
      const collection = config && config.blocks_collection && config.blocks_collection
      if (!collection) return {}

      if (state.page === "default") return collection[state.page] || {}
      let containers

      if (collection[state.page]) {
        containers = {
          ...collection.default,
          ...collection[state.page],
        }
      } else {
        containers = { ...collection.default }
      }

      return mergeDeep(containers, ...getters.sources)
    },
    fullDomainLink(state) {
      if (!state.website || state.loadingPackages) return null
      return "https://" + state.website.domainname
    },

    website(state) {
      return state.website
    },
    restartTrigger(state) {
      return state.restartTrigger
    },
    config(state) {
      return mergeDeep(state.config || {}, state.currentPage || {})
    },
    availablePackages(state) {
      return state.availablePackages
    },
    stringifiedConfig(state) {
      return JSON.stringify(state.config)
    },
    magentoLoaded(state) {
      return state.magentoLoaded
    },
    ready(state) {
      return state.ready
    },
    loading(state) {
      return state.loading || state.loadingPackages
    },
    page(state) {
      return state.page
    },
    containers(state) {
      return state.containers
    },
  },
  actions: {
    async setState({ state, commit }, {blockId, blockState}) {
      state.states[blockId] = blockState

      state.states = {...state.states}
      state.config = {...state.config}
      commit("triggerBlocksRefetch")
    },
    async context({ state, commit }, context) {
      if (context) {
        state.context = { ...mergeDeep(state.context || {}, context) }
        if (state.context.categoryId && state.context.productId) {
          commit("triggerBlocksRefetch")
        }
      } else {
        state.context = null
        commit("triggerBlocksRefetch")
      }
    },

    async saveConfiguration({ state }) {
      return await Promise.all(state.saveListeners.map(async saver => await saver()))
    },
    async pushSaveListener({ commit, state }, func) {
      if (state.saveListeners.includes(func)) return;
      state.saveListeners.push(func)
    },
    async removeSaveListener({ commit, state }, func) {
      state.saveListeners.splice(state.saveListeners.indexOf(func), 1)
    },
    async loadConfigurations({ state }) {
      return await Promise.all(state.loaderListeners.map(async saver => await saver()))
    },
    async pushConfigLoader({ commit, state }, func) {
      if (state.loaderListeners.includes(func)) return;
      state.loaderListeners.push(func)
    },
    async removeConfigLoader({ commit, state }, func) {
      state.loaderListeners.splice(state.loaderListeners.indexOf(func), 1)
    },

    async setBlockConfig({ dispatch, commit, getters }, { id, role, config }) {
      const page = getters['page']

      const allBlocks = getters["config"].blocks_collection[page][role].slice()
      const i = allBlocks.findIndex(block => block.id === id)
      if (i < 0) throw "not found"

      allBlocks[i].config = {...allBlocks[i].config, ...config}
      dispatch("overwriteConfig", {
        path: `blocks_collection.${page}.${role}`,
        value: allBlocks,
      })
      commit("triggerBlocksRefetch", id)
    },
    async reloadedBlock({ state }, id) {
      if (state.blockReload[id]) delete state.blockReload[id]
    },
    async requestBlockReload({ state }, blockId) {
      state.blockReload[blockId] = true
      state.blockReload = { ...state.blockReload }
    },
    async overwriteBlock({ commit, getters }, { role, id, block }) {
      const page = getters['page']

      const allBlocks = getters["config"].blocks_collection[page][role].slice()
      const i = allBlocks.findIndex(block => block.id === id)
      if (i < 0) throw "not found"

      allBlocks.splice(i, { ...mergeDeep(allBlocks[i], block) })
      commit("config", {
        blocks_collection: {
          [page]: {
            [role]: allBlocks,
          },
        },
      })
    },
    async addBlock({ commit, state }, { role, page, blocks }) {
      const config = {
        blocks_collection: {
          [page]: {
            [role]: blocks,
          },
        },
      }

      if (!state.currentPage) {
        commit("config", config)
      } else {
        state.currentPage = {...mergeDeep(state.currentPage, config)}
      }
    },
    async removeConfigKeys({ commit, getters }, keys) {
      const config = getters["config"]

      // path: link.1.path.to.value
      keys.forEach(path => {
        const splitted = path.split(".")
        const index = splitted.pop()
        const ref = getPathReference(config, splitted.join("."))
        delete ref[index]
      })

      commit("nonRecursiveConfig", {...config})
    },
    async overwriteBlockContent({ dispatch }, { id, role, content }) {
      dispatch('overwriteBlock', {
        id, role, block: { content: unmappObject(content) },
      })
    },

    async overwriteConfig({ commit, getters, state }, { path, value }) {
      commit("overwriteConfig", { path, value })
      commit("nonRecursiveConfig", {...state.config})
    },
    async removeBlock({ commit, state }, { role, id }) {
      const newConfig = {...state.config}

      const target = newConfig.blocks_collection[state.page][role]
      const i = target.findIndex(block => block.id === id)
      if (i > -1) target.splice(i, 1)

      commit("nonRecursiveConfig", newConfig)
    },
    async load({ state }, uid) {
      if (uid) state.websiteUid = uid

      if (!state.website) {
        state.loading = true
      }
      state.website = (await axios.get(`/api/websites/${state.websiteUid}`)).data
      state.config = state.website.magento_config && JSON.parse(state.website.magento_config)
      console.log("INITIAL CONFIG!: ", clone(state.config))
      state.initial_config = state.config ? clone(state.config) : null
      state.inital_colors_config = (state.config && state.config.tailwindConfig && state.config.tailwindConfig.extend && state.config.tailwindConfig.extend.colors) ? clone(state.config.tailwindConfig.extend.colors) : null
      state.loading = false
    },
    async loadAvailablePackages({ state }) {
      state.loadingPackages = true
      state.availablePackages = (await axios.get(`/api/editor-packages`)).data.results
      state.loadingPackages = false
    },
  },
}

